import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, Monitor, Tablet, Smartphone, ArrowLeftRight, CheckCircle, AlertTriangle, Clock, Upload, Download, Cpu, Signal } from 'lucide-react';
import { Card } from '../components/ui/Card';
import {
  fetchSyncStatus,
  fetchSyncHistory,
  triggerSync,
  removeDevice,
  triggerCrossPlatformSync,
  fetchCrossPlatformDevices,
  type SyncDevice,
  type SyncLog,
} from '../services/sync-service';
import {
  registerCurrentDevice,
  fetchSyncSettings,
  updateSyncSettings,
  type SyncSettings,
} from '../services/sync-share-service';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmbeddedSkillRail } from '@/components/skill';

const CrossPlatformSync: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'devices' | 'history' | 'settings'>('devices');
  const [isSyncing, setIsSyncing] = useState(false);
  const [devices, setDevices] = useState<SyncDevice[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncLog[]>([]);
  const [syncStatus, setSyncStatus] = useState<string>('synced');
  const [lastSync, setLastSync] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncSettings, setSyncSettings] = useState<SyncSettings | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await registerCurrentDevice().catch(() => undefined);

      const [statusResult, historyResult, devicesResult, settingsResult] = await Promise.allSettled([
        fetchSyncStatus(),
        fetchSyncHistory(),
        fetchCrossPlatformDevices(),
        fetchSyncSettings(),
      ]);

      if (statusResult.status === 'fulfilled') {
        setSyncStatus(statusResult.value.status);
        setLastSync(statusResult.value.lastSync);
      }

      if (historyResult.status === 'fulfilled') {
        setSyncHistory(historyResult.value.map(item => ({
          id: item.id,
          action: item.action,
          device: item.device,
          timestamp: new Date(item.timestamp),
          tabsAffected: item.tabsAffected,
        })));
      }

      if (devicesResult.status === 'fulfilled') {
        setDevices(devicesResult.value);
      } else {
        setDevices([]);
      }

      if (settingsResult.status === 'fulfilled') {
        setSyncSettings(settingsResult.value);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sync data');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await triggerSync();
      await triggerCrossPlatformSync();
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRetrySync = async (deviceId: string) => {
    setDevices(prev =>
      prev.map(d =>
        d.id === deviceId ? { ...d, syncStatus: 'syncing' as const } : d
      )
    );
    try {
      await triggerSync();
      await loadData();
    } catch (err) {
      setDevices(prev =>
        prev.map(d =>
          d.id === deviceId ? { ...d, syncStatus: 'error' as const } : d
        )
      );
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    try {
      await removeDevice(deviceId);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      console.error('Failed to remove device:', err);
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'desktop': return Monitor;
      case 'tablet': return Tablet;
      case 'mobile': return Smartphone;
      default: return Monitor;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'synced': return CheckCircle;
      case 'syncing': return ArrowLeftRight;
      case 'pending': return Clock;
      case 'error': return AlertTriangle;
      default: return CheckCircle;
    }
  };

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'synced': return { bg: 'bg-success-500', badge: 'bg-success-500/20 text-success-500' };
      case 'syncing': return { bg: 'bg-info-500', badge: 'bg-info-500/20 text-info-500' };
      case 'pending': return { bg: 'bg-warning-500', badge: 'bg-warning-500/20 text-warning-500' };
      case 'error': return { bg: 'bg-error-500', badge: 'bg-error-500/20 text-error-500' };
      default: return { bg: 'bg-khiimori-bg-tertiary', badge: 'bg-khiimori-bg-tertiary/20 text-khiimori-text-muted' };
    }
  };

  const handleSettingChange = async (updates: Partial<SyncSettings>) => {
    try {
      const next = await updateSyncSettings(updates);
      setSyncSettings(next);
    } catch (err) {
      console.error('Failed to update sync settings:', err);
    }
  };

  const getRelativeTime = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <PageLayout
      title="Cross Platform Sync"
      description="Sync your tabs across devices and platforms"
      icon={Cloud}
    >

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Connected Devices', value: devices.length, icon: Cpu, bgClass: 'bg-primary-500' },
          { label: 'Synced Tabs', value: devices.reduce((sum, d) => sum + d.tabCount, 0), icon: Cloud, bgClass: 'bg-info-500' },
          { label: 'Sync Operations', value: syncHistory.length, icon: ArrowLeftRight, bgClass: 'bg-success-500' },
          { label: 'Last Sync', value: lastSync ? getRelativeTime(new Date(lastSync)) : 'Never', icon: Clock, bgClass: 'bg-warning-500' },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card hover>
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-xl ${stat.bgClass}`}>
                    <Icon className="h-6 w-6 text-khiimori-text-primary" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-khiimori-text-primary">
                      {stat.value}
                    </div>
                    <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 mb-6">
        {[
          { id: 'devices' as const, label: 'Devices', icon: Cpu },
          { id: 'history' as const, label: 'Sync History', icon: Clock },
          { id: 'settings' as const, label: 'Settings', icon: Signal },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary dark:hover:bg-khiimori-bg-tertiary'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <EmbeddedSkillRail
        cardClassName="mb-6"
        title="Sync skills"
        restrictCategories={['collaborate']}
      />

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'devices' && (
          <motion.div
            key="devices"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {loading ? (
              <div className="col-span-full text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="mt-2 text-khiimori-text-secondary">Loading devices...</p>
              </div>
            ) : devices.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Cpu className="h-12 w-12 mx-auto text-khiimori-text-muted" />
                <p className="mt-2 text-khiimori-text-secondary">No devices found</p>
                <button
                  onClick={loadData}
                  className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Refresh
                </button>
              </div>
            ) : (
              devices.map((device, index) => {
                const DeviceIcon = getDeviceIcon(device.type);
                const StatusIcon = getStatusIcon(device.syncStatus);
                const statusClasses = getStatusClasses(device.syncStatus);

                return (
                  <motion.div
                    key={device.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card hover className="h-full">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-xl ${statusClasses.bg}`}>
                          <DeviceIcon className="h-6 w-6 text-khiimori-text-primary" />
                        </div>
                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${statusClasses.badge}`}>
                          <StatusIcon className="h-4 w-4" />
                          <span>{device.syncStatus}</span>
                        </div>
                      </div>

                      <h3 className="font-bold text-khiimori-text-primary mb-2">{device.name}</h3>

                      <div className="space-y-2 text-sm text-khiimori-text-secondary">
                        <div className="flex items-center justify-between">
                          <span>Tabs synced</span>
                          <span className="font-semibold text-khiimori-text-primary">{device.tabCount}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Last sync</span>
                          <span>{getRelativeTime(device.lastSync)}</span>
                        </div>
                      </div>

                      {device.syncStatus === 'error' && (
                        <button
                          onClick={() => handleRetrySync(device.id)}
                          className="w-full mt-4 px-4 py-2 bg-error-500 text-khiimori-text-primary rounded-lg hover:bg-error-600 transition-colors text-sm font-medium"
                        >
                          Retry Sync
                        </button>
                      )}

                      <button
                        onClick={() => handleRemoveDevice(device.id)}
                        className="w-full mt-2 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary dark:hover:bg-khiimori-bg-tertiary transition-colors text-sm font-medium"
                      >
                        Remove Device
                      </button>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        )}

        {activeTab === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="mt-2 text-khiimori-text-secondary">Loading history...</p>
              </div>
            ) : syncHistory.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 mx-auto text-khiimori-text-muted" />
                <p className="mt-2 text-khiimori-text-secondary">No sync history yet</p>
              </div>
            ) : (
              syncHistory.map((log, index) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card hover>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-3 bg-success-500 rounded-xl">
                          <ArrowLeftRight className="h-5 w-5 text-khiimori-text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-khiimori-text-primary">{log.action}</h3>
                          <p className="text-sm text-khiimori-text-secondary">{log.device}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-khiimori-text-primary">{log.tabsAffected} tabs</div>
                        <div className="text-xs text-khiimori-text-muted">{log.timestamp.toLocaleString()}</div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            key="settings"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card>
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-6">Sync Settings</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated rounded-lg">
                  <div>
                    <h4 className="font-medium text-khiimori-text-primary">Auto Sync</h4>
                    <p className="text-sm text-khiimori-text-secondary">Automatically sync changes in real-time</p>
                  </div>
                  <button
                    onClick={() => handleSettingChange({ auto_sync: !syncSettings?.auto_sync })}
                    className={`w-12 h-6 rounded-full relative transition-colors ${syncSettings?.auto_sync ? 'bg-success-500' : 'bg-khiimori-bg-tertiary'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${syncSettings?.auto_sync ? 'right-1' : 'left-1'}`}></div>
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated rounded-lg">
                  <div>
                    <h4 className="font-medium text-khiimori-text-primary">Sync Frequency</h4>
                    <p className="text-sm text-khiimori-text-secondary">How often to check for updates</p>
                  </div>
                  <select
                    value={syncSettings?.sync_frequency ?? 'realtime'}
                    onChange={(e) => handleSettingChange({ sync_frequency: e.target.value })}
                    className="px-4 py-2 bg-khiimori-bg-elevated rounded-lg border border-khiimori-border text-khiimori-text-primary"
                  >
                    <option value="realtime">Real-time</option>
                    <option value="5m">Every 5 minutes</option>
                    <option value="15m">Every 15 minutes</option>
                    <option value="manual">Manual only</option>
                  </select>
                </div>
                <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated rounded-lg">
                  <div>
                    <h4 className="font-medium text-khiimori-text-primary">Sync on Mobile Data</h4>
                    <p className="text-sm text-khiimori-text-secondary">Allow sync when not on WiFi</p>
                  </div>
                  <button
                    onClick={() => handleSettingChange({ sync_on_mobile_data: !syncSettings?.sync_on_mobile_data })}
                    className={`w-12 h-6 rounded-full relative transition-colors ${syncSettings?.sync_on_mobile_data ? 'bg-success-500' : 'bg-khiimori-bg-tertiary'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${syncSettings?.sync_on_mobile_data ? 'right-1' : 'left-1'}`}></div>
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Status */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8"
      >
        <Card className="bg-gradient-glass border-primary-400/30 dark:border-primary-400/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-primary-500 rounded-xl">
                <Cloud className="h-6 w-6 text-khiimori-text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-khiimori-text-primary">
                  {syncStatus === 'synced' ? 'All Systems Synchronized' : 'Sync In Progress'}
                </h3>
                <p className="text-sm text-khiimori-text-secondary">
                  {syncStatus === 'synced' ? 'Your tabs are up-to-date across all devices' : `Last sync: ${lastSync ? new Date(lastSync).toLocaleString() : 'Never'}`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${syncStatus === 'synced' ? 'bg-success-500' : 'bg-warning-500'} animate-pulse`}></div>
              <span className={`text-sm font-medium ${syncStatus === 'synced' ? 'text-success-500 dark:text-success-400' : 'text-warning-500 dark:text-warning-400'}`}>
                {syncStatus === 'synced' ? 'Online' : 'Syncing...'}
              </span>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default CrossPlatformSync;